import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import {
  CheckboxField,
  SelectField,
  TextAreaField,
  TextField,
} from '../components/Field';
import {
  ApiError,
  createDraft,
  getBuilderOptions,
  previewDraft,
  submitDraft,
} from '../lib/api';
import type { CampaignRead, SkillChallengeOption } from '../lib/api';
import {
  BUILDER_STEP_FIELDS,
  EMPTY_BUILDER,
  randToCents,
  toDraftPayload,
  validateBuilderAll,
  validateBuilderStep,
} from '../lib/builderValidation';
import type { BuilderErrors, BuilderValues } from '../lib/builderValidation';

const STEP_LABELS = [
  'Brand & partner',
  'Prize',
  'Skill challenge',
  'Window & tracking',
  'Consent & opt-ins',
  'Review & submit',
];

const PREVIEW_STEP = STEP_LABELS.length - 1;

function formatRand(cents?: number | null): string {
  if (cents == null) return '—';
  return `R${(cents / 100).toLocaleString('en-ZA')}`;
}

/**
 * Self-serve campaign builder wizard (epic-10, v1-10-2).
 *
 * Guides a partner through configuring a campaign DRAFT and submitting it into
 * the operator review state machine (draft -> submitted). It never bypasses the
 * operator approve gate — submit only parks the campaign in review.
 *
 * Backend gates the whole surface behind the internal-staff auth stub
 * (fail-closed in prod until v1-2-6). When that returns 501, the wizard shows a
 * friendly "not yet available — contact us" message rather than breaking.
 */
export function CampaignBuilder({ partnerId = '' }: { partnerId?: string }) {
  const [values, setValues] = useState<BuilderValues>({
    ...EMPTY_BUILDER,
    partner_id: partnerId,
  });
  const [errors, setErrors] = useState<BuilderErrors>({});
  const [step, setStep] = useState(0);
  const [skills, setSkills] = useState<SkillChallengeOption[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [violations, setViolations] = useState<string[]>([]);
  const [result, setResult] = useState<CampaignRead | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let alive = true;
    getBuilderOptions()
      .then((opts) => {
        if (alive) setSkills(opts.skill_challenge_types);
      })
      .catch((err) => {
        if (alive)
          setOptionsError(
            err instanceof ApiError
              ? err.message
              : 'Could not load the campaign builder.',
          );
      });
    return () => {
      alive = false;
    };
  }, []);

  function set<K extends keyof BuilderValues>(name: K, value: BuilderValues[K]) {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  function focusHeading() {
    requestAnimationFrame(() => headingRef.current?.focus());
  }

  function goNext() {
    const stepErrors = validateBuilderStep(step, values);
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }
    setStep((s) => Math.min(s + 1, PREVIEW_STEP));
    setViolations([]);
    focusHeading();
  }

  function goBack() {
    setSubmitError(null);
    setStep((s) => Math.max(s - 1, 0));
    focusHeading();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setViolations([]);

    const allErrors = validateBuilderAll(values);
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      const firstBadStep = BUILDER_STEP_FIELDS.findIndex((fields) =>
        fields.some((f) => allErrors[f]),
      );
      if (firstBadStep >= 0) {
        setStep(firstBadStep);
        focusHeading();
      }
      return;
    }

    setSubmitting(true);
    try {
      // Create the draft, run the server compliance preview, then submit it
      // into the review state machine.
      const draft = await createDraft(toDraftPayload(values));
      const preview = await previewDraft(draft.id);
      if (!preview.ready_to_submit) {
        setViolations(preview.violations);
        setSubmitError('Your campaign needs a few changes before we can submit it.');
        return;
      }
      const submitted = await submitDraft(draft.id);
      setResult(submitted);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
        const detail = err.detail as { violations?: string[] } | undefined;
        if (detail?.violations) setViolations(detail.violations);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <section id="campaign-builder" className="section bg-white">
        <div className="container-content">
          <div
            className="mx-auto max-w-2xl rounded-3xl border border-line bg-surface-alt p-8 text-center shadow-card sm:p-10"
            role="status"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-aurora text-white">
              <Icon name="check" className="h-7 w-7" />
            </span>
            <h2 className="mt-6 font-heading text-2xl font-bold text-ink-900 sm:text-3xl">
              Submitted for review
            </h2>
            <p className="mt-3 text-ink-500">
              <strong>{result.name}</strong> is now with our team. Every campaign is
              reviewed and a signed agreement is in place before it goes live —
              winners are always decided by skill. We'll be in touch with next
              steps.
            </p>
            <p className="mt-4 text-sm text-ink-500">
              Reference: <span className="font-mono">{result.id}</span>
            </p>
            <a
              href="/partner"
              className="mt-6 inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              View my campaigns
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="campaign-builder" className="section bg-white">
      <div className="container-content">
        <div className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Campaign Builder</span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
            Build your campaign
          </h2>
          <p className="mt-4 text-lg text-ink-500">
            Configure your prize, skill challenge and tracking, then submit for
            review. Every competition is decided by skill — never a draw.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-3xl">
          {optionsError ? (
            <div
              role="alert"
              className="rounded-3xl border border-line bg-surface-alt p-8 text-center shadow-card"
            >
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-700/10 text-primary-700">
                <Icon name="shield" className="h-6 w-6" />
              </span>
              <p className="mt-4 text-ink-700">{optionsError}</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              className="rounded-3xl border border-line bg-white p-6 shadow-card sm:p-8"
            >
              {/* Progress */}
              <div className="mb-8">
                <p className="font-heading text-sm font-semibold text-primary-700">
                  Step {step + 1} of {STEP_LABELS.length}
                </p>
                <ol className="mt-3 flex gap-2" aria-hidden>
                  {STEP_LABELS.map((label, i) => (
                    <li
                      key={label}
                      className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                        i <= step ? 'bg-aurora' : 'bg-surface-muted'
                      }`}
                    />
                  ))}
                </ol>
                <h3
                  ref={headingRef}
                  tabIndex={-1}
                  className="mt-4 font-heading text-xl font-semibold text-ink-900 focus:outline-none"
                >
                  {STEP_LABELS[step]}
                </h3>
              </div>

              {/* Step 1 — Brand & partner */}
              {step === 0 && (
                <div className="grid gap-5">
                  {/* A logged-in partner's id is taken from their session (and the
                      backend forces it from the token anyway) — only show the
                      field when it isn't pre-filled (e.g. operator use). */}
                  {!partnerId && (
                    <TextField
                      id="partner_id"
                      label="Partner ID"
                      required
                      hint="Your partner account identifier."
                      value={values.partner_id}
                      error={errors.partner_id}
                      onChange={(e) => set('partner_id', e.target.value)}
                    />
                  )}
                  <TextField
                    id="name"
                    label="Campaign name"
                    required
                    value={values.name}
                    error={errors.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="e.g. Win an Acme SUV"
                  />
                  <TextField
                    id="brand_assets_url"
                    label="Brand / product asset link"
                    type="url"
                    hint="Recommended. A link to your logo or product images."
                    value={values.brand_assets_url}
                    error={errors.brand_assets_url}
                    onChange={(e) => set('brand_assets_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              )}

              {/* Step 2 — Prize */}
              {step === 1 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <TextField
                      id="prize_title"
                      label="Prize title"
                      required
                      value={values.prize_title}
                      error={errors.prize_title}
                      onChange={(e) => set('prize_title', e.target.value)}
                      placeholder="e.g. Acme SUV 2026"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <TextAreaField
                      id="prize_description"
                      label="Prize description"
                      required
                      value={values.prize_description}
                      error={errors.prize_description}
                      onChange={(e) => set('prize_description', e.target.value)}
                      placeholder="Describe the prize the winner receives."
                    />
                  </div>
                  <TextField
                    id="prize_value_rand"
                    label="Prize value (Rand)"
                    required
                    value={values.prize_value_rand}
                    error={errors.prize_value_rand}
                    onChange={(e) => set('prize_value_rand', e.target.value)}
                    placeholder="e.g. 750,000"
                    inputMode="decimal"
                  />
                  <SelectField
                    id="prize_currency"
                    label="Currency"
                    required
                    value={values.prize_currency}
                    error={errors.prize_currency}
                    onChange={(e) => set('prize_currency', e.target.value)}
                  >
                    <option value="ZAR">ZAR (South African Rand)</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </SelectField>
                  <div className="rounded-2xl bg-surface-alt p-4 text-sm text-ink-500 sm:col-span-2">
                    Corporate prizes are always non-cash and given away to a winner
                    decided by skill.
                  </div>
                </div>
              )}

              {/* Step 3 — Skill challenge */}
              {step === 2 && (
                <div className="grid gap-5">
                  <SelectField
                    id="skill_challenge_type"
                    label="Skill challenge"
                    required
                    hint="The winner is decided by this skill challenge — never a draw, lottery or chance."
                    value={values.skill_challenge_type}
                    error={errors.skill_challenge_type}
                    onChange={(e) => set('skill_challenge_type', e.target.value)}
                  >
                    <option value="">Select a skill challenge</option>
                    {skills.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </SelectField>
                  <div className="rounded-2xl bg-surface-alt p-5">
                    <p className="flex items-center gap-2 font-heading text-sm font-semibold text-ink-900">
                      <Icon name="shield" className="h-5 w-5 text-primary-700" />
                      Skill-based by design
                    </p>
                    <p className="mt-2 text-sm text-ink-500">
                      All competitions on the platform are decided solely by
                      published skill criteria. Draws, lotteries, raffles and games
                      of chance are not offered.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 4 — Window & tracking */}
              {step === 3 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField
                    id="starts_at"
                    label="Start date"
                    type="date"
                    value={values.starts_at}
                    error={errors.starts_at}
                    onChange={(e) => set('starts_at', e.target.value)}
                  />
                  <TextField
                    id="ends_at"
                    label="End date"
                    type="date"
                    value={values.ends_at}
                    error={errors.ends_at}
                    onChange={(e) => set('ends_at', e.target.value)}
                  />
                  <div className="sm:col-span-2">
                    <TextAreaField
                      id="tracking_notes"
                      label="Tracking preferences"
                      hint="Where will QR codes and tracked links be used? (showroom desks, online, etc.)"
                      value={values.tracking_notes}
                      error={errors.tracking_notes}
                      onChange={(e) => set('tracking_notes', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Step 5 — Consent & opt-ins */}
              {step === 4 && (
                <div className="grid gap-5">
                  <div className="rounded-2xl bg-surface-alt p-5">
                    <p className="font-heading text-sm font-semibold text-ink-900">
                      Optional add-ons
                    </p>
                    <div className="mt-3 space-y-3">
                      <CheckboxField
                        id="leads_capture_enabled"
                        checked={values.leads_capture_enabled}
                        onChange={(c) => set('leads_capture_enabled', c)}
                        label={
                          <>
                            Capture <strong>opt-in leads</strong> from participants
                            who consent to partner contact for this campaign.
                          </>
                        }
                      />
                      <CheckboxField
                        id="ai_optin_enabled"
                        checked={values.ai_optin_enabled}
                        onChange={(c) => set('ai_optin_enabled', c)}
                        label={
                          <>
                            I'm interested in <strong>AI marketing support</strong>{' '}
                            for campaign content, copy and creative.
                          </>
                        }
                      />
                    </div>
                  </div>
                  <CheckboxField
                    id="consent_terms"
                    checked={values.consent_terms}
                    error={errors.consent_terms}
                    onChange={(c) => set('consent_terms', c)}
                    label={
                      <>
                        I have authority to submit this campaign and understand that
                        all competitions are skill-based — the prize is guaranteed to
                        be given away to a winner decided by skill, and participant
                        details are only shared with explicit, recorded consent.
                      </>
                    }
                  />
                </div>
              )}

              {/* Step 6 — Review & submit */}
              {step === PREVIEW_STEP && (
                <div className="grid gap-4">
                  <p className="text-ink-500">
                    Review your campaign before submitting it for our team's review.
                  </p>
                  <dl className="divide-y divide-line rounded-2xl border border-line">
                    {(
                      [
                        ['Campaign', values.name],
                        ['Prize', values.prize_title],
                        [
                          'Prize value',
                          formatRand(randToCents(values.prize_value_rand)) +
                            (values.prize_currency !== 'ZAR'
                              ? ` (${values.prize_currency})`
                              : ''),
                        ],
                        [
                          'Skill challenge',
                          skills.find((s) => s.value === values.skill_challenge_type)
                            ?.label ?? values.skill_challenge_type,
                        ],
                        [
                          'Window',
                          values.starts_at && values.ends_at
                            ? `${values.starts_at} → ${values.ends_at}`
                            : 'To be confirmed',
                        ],
                        [
                          'Opt-in leads',
                          values.leads_capture_enabled ? 'Yes' : 'No',
                        ],
                        [
                          'AI marketing support',
                          values.ai_optin_enabled ? 'Yes' : 'No',
                        ],
                      ] as [string, string][]
                    ).map(([label, value]) => (
                      <div
                        key={label}
                        className="flex justify-between gap-4 px-4 py-3 text-sm"
                      >
                        <dt className="font-medium text-ink-500">{label}</dt>
                        <dd className="text-right font-medium text-ink-900">
                          {value || '—'}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {violations.length > 0 && (
                <ul
                  role="alert"
                  className="mt-6 list-disc space-y-1 rounded-xl bg-accent-600/10 px-6 py-3 text-sm font-medium text-accent-600"
                >
                  {violations.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              )}

              {submitError && violations.length === 0 && (
                <p
                  role="alert"
                  className="mt-6 rounded-xl bg-accent-600/10 px-4 py-3 text-sm font-medium text-accent-600"
                >
                  {submitError}
                </p>
              )}

              {/* Controls */}
              <div className="mt-8 flex items-center justify-between gap-3">
                {step > 0 ? (
                  <button
                    type="button"
                    onClick={goBack}
                    className="btn-secondary"
                    disabled={submitting}
                  >
                    Back
                  </button>
                ) : (
                  <span />
                )}

                {step < PREVIEW_STEP ? (
                  <button type="button" onClick={goNext} className="btn-primary">
                    Continue
                    <Icon name="arrow-right" className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn-warm"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting…' : 'Submit for review'}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default CampaignBuilder;
